// Firebase Auth context for the assessment app.
//
// Public takers sign in passwordless: Google popup or email magic-link.
// APC staff/coaches use the same Google provider and are recognized by an
// @aportlandcareer.com email plus an existing coaches/{uid} Firestore doc.
//
// Tier entitlement (FREE | ENHANCED | PREMIUM) is read from a custom claim
// set server-side by the Stripe webhook. We fall back to FREE.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, googleProvider } from '../lib/firebase';
import { activeIdentity, onIdentityChanged } from '../lib/identity';
import { takeSignInTicket } from '../lib/signInTicket';

const AuthContext = createContext(null);

const EMAIL_FOR_SIGNIN_KEY = 'cosmic-magic-link-email';
const APC_DOMAIN = '@aportlandcareer.com';

function magicLinkSettings() {
  return {
    url: `${window.location.origin}/cosmic-login`,
    handleCodeInApp: true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tier, setTier] = useState('FREE');
  const [isCoach, setIsCoach] = useState(false);
  const [loading, setLoading] = useState(true);

  // Resolve tier (custom claim) and coach status for a signed-in user.
  const resolveClaims = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setTier('FREE');
      setIsCoach(false);
      return;
    }
    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      const claimTier = tokenResult.claims.tier;
      setTier(typeof claimTier === 'string' ? claimTier.toUpperCase() : 'FREE');
    } catch (err) {
      console.warn('Failed to read tier claim:', err);
      setTier('FREE');
    }

    // Staff/coach recognition: APC email + coaches/{uid} doc exists.
    try {
      const email = (firebaseUser.email || '').toLowerCase();
      if (email.endsWith(APC_DOMAIN)) {
        const coachSnap = await getDoc(doc(db, 'coaches', firebaseUser.uid));
        setIsCoach(coachSnap.exists());
      } else {
        setIsCoach(false);
      }
    } catch (err) {
      console.warn('Failed to resolve coach status:', err);
      setIsCoach(false);
    }
  }, []);

  // Cross-app sign-in from the CRM (a coach viewing a client's result) or the
  // portal's self-test (a coach's test client): S6.3, a single-use ticket on
  // ?signin=, taken off the URL first and spent once (lib/signInTicket.js), so
  // nobody sees a second Google login and no reusable token sits in a URL.
  useEffect(() => {
    void takeSignInTicket({
      location: window.location,
      replaceState: (next) => window.history.replaceState({}, '', next),
      redeem: async (data) => (await httpsCallable(functions, 'redeemSignInTicket')(data)).data,
      signIn: (token) => signInWithCustomToken(auth, token),
    });
  }, []);

  useEffect(() => {
    // S5b.3: the user a flow sees comes from the identity module: the launch
    // user while this tab holds a launch, else the default instance's.
    const unsubAuth = onIdentityChanged(async ({ user: firebaseUser }) => {
      setUser(firebaseUser);
      await resolveClaims(firebaseUser);
      setLoading(false);
    });
    // Picks up refreshed custom claims (e.g. after a payment grants a tier).
    const unsubToken = onIdTokenChanged(auth, async (firebaseUser) => {
      // A launched tab's claims are the launch user's, never the default instance's.
      if (firebaseUser && activeIdentity().mode !== 'launch') await resolveClaims(firebaseUser);
    });
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, [resolveClaims]);

  // Ensure an assessmentUsers/{uid} profile doc exists for a public taker.
  const ensureUserProfile = useCallback(async (firebaseUser, extra = {}) => {
    if (!firebaseUser) return;
    const ref = doc(db, 'assessmentUsers', firebaseUser.uid);
    const snap = await getDoc(ref);
    const base = {
      email: firebaseUser.email || extra.email || null,
      displayName: firebaseUser.displayName || extra.displayName || null,
      updatedAt: serverTimestamp(),
    };
    if (!snap.exists()) {
      await setDoc(ref, { ...base, tier: 'FREE', createdAt: serverTimestamp() });
    } else {
      await setDoc(ref, base, { merge: true });
    }
  }, []);

  // Finish a redirect-based sign-in (the popup fallback below). Runs once on
  // mount; when the browser returns from the provider, getRedirectResult resolves
  // with the user so we can create the profile doc. onAuthStateChanged handles
  // setUser + resolveClaims, so this only needs the profile side-effect. Declared
  // AFTER ensureUserProfile so the dependency reference is not in a temporal dead
  // zone (a use-before-declaration here white-screens the whole app).
  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result && result.user) {
          return ensureUserProfile(result.user);
        }
      })
      .catch((err) => {
        if (err && err.code !== 'auth/no-auth-event') {
          console.warn('Redirect sign-in did not complete:', err);
        }
      });
  }, [ensureUserProfile]);

  // Popup sign-in fails to *complete* when the browser blocks third-party
  // cookies/storage (Safari ITP, Brave, Chrome with 3p cookies off): the Firebase
  // auth handshake runs on the authDomain origin (apc-intake-staging.firebaseapp.com),
  // which is cross-origin to this app (apc-assessment-staging.web.app), so the
  // credential can't post back and the popup throws. These are the codes that
  // signal "popup path is unavailable here" -> fall back to a full-page redirect,
  // which survives partitioned storage because it's a top-level navigation.
  const POPUP_UNAVAILABLE_CODES = new Set([
    'auth/popup-blocked',
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/web-storage-unsupported',
    'auth/operation-not-supported-in-this-environment',
    'auth/internal-error',
  ]);

  // Shared federated sign-in. Turns the "same email, different provider" collision
  // into a friendly, actionable error instead of a raw Firebase code. We do NOT
  // auto-link by email (that path is a known account-takeover vector).
  const signInWithProvider = useCallback(async (provider) => {
    try {
      const result = await signInWithPopup(auth, provider);
      await ensureUserProfile(result.user);
      await resolveClaims(result.user);
      return result.user;
    } catch (err) {
      if (err && err.code === 'auth/account-exists-with-different-credential') {
        const friendly = new Error(
          'You already have an account with this email. Please sign in using the method you used the first time (Google, Microsoft, or the email sign-in link).'
        );
        friendly.code = err.code;
        throw friendly;
      }
      // Popup couldn't complete (commonly a coach on a browser that blocks
      // third-party cookies). Retry via redirect; getRedirectResult below finishes
      // the profile/claims setup when the browser returns to the app.
      if (err && POPUP_UNAVAILABLE_CODES.has(err.code)) {
        await signInWithRedirect(auth, provider);
        return null;
      }
      throw err;
    }
  }, [ensureUserProfile, resolveClaims]);

  const signInWithGoogle = useCallback(
    () => signInWithProvider(googleProvider),
    [signInWithProvider]
  );

  // Send the passwordless sign-in email through our branded Cloud Function
  // (Resend + on-brand template) rather than Firebase's default email template.
  const sendMagicLink = useCallback(async (email) => {
    const sendLink = httpsCallable(functions, 'sendSignInLink');
    await sendLink({ email, continueUrl: magicLinkSettings().url });
    window.localStorage.setItem(EMAIL_FOR_SIGNIN_KEY, email);
  }, []);

  // Call on load (e.g. on the login route) to finish a magic-link sign-in.
  const completeMagicLinkSignIn = useCallback(async () => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return null;
    let email = window.localStorage.getItem(EMAIL_FOR_SIGNIN_KEY);
    if (!email) {
      email = window.prompt('Please confirm your email to finish signing in');
    }
    if (!email) return null;
    const result = await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY);
    await ensureUserProfile(result.user, { email });
    await resolveClaims(result.user);
    return result.user;
  }, [ensureUserProfile, resolveClaims]);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  // Force a token refresh so a newly granted tier claim is picked up immediately.
  const refreshClaims = useCallback(async () => {
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      await resolveClaims(auth.currentUser);
    }
  }, [resolveClaims]);

  const value = {
    user,
    tier,
    isCoach,
    loading,
    isAuthenticated: !!user,
    signInWithGoogle,
    sendMagicLink,
    completeMagicLinkSignIn,
    isMagicLink: () => isSignInWithEmailLink(auth, window.location.href),
    signOut,
    refreshClaims,
    ensureUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthContext;
